import {
    fetchGetStateByStateKey,
    fetchPutApplication,
    fetchPutState,
    PutApplicationRequestBody,
    PutStateRequestBody
} from '@iwoca/lapi-client/edge';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type TPutApplicationRequestBody = NonNullable<
    NonNullable<PutApplicationRequestBody>['data']
>['requested_products'][number];

export type TApplicationWithTurnover = TPutApplicationRequestBody & {
    company_turnover: number;
};

export const useCreateNewApplication = (stateKey: string) => {
    const queryClient = useQueryClient();

    const createApplicationMutation = useMutation({
        mutationFn: (application: TPutApplicationRequestBody) =>
            fetchPutApplication({
                stateKey: stateKey,
                body: {
                    data: {
                        requested_products: [application]
                    }
                }
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['application', stateKey]
            });
        }
    });

    const saveCustomerStateMutation = useMutation({
        mutationFn: (state: PutStateRequestBody) =>
            fetchPutState({
                stateKey: stateKey,
                body: state
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['customer_state', stateKey]
            });
        },
        onError: () => {
            throw Error('Failed to update customer state');
        }
    });

    const createNewApplication = async (
        application: TApplicationWithTurnover
    ) => {
        try {
            const { company_turnover, ...applicationWithoutTurnover } =
                application;

            const customerState = await queryClient.fetchQuery({
                queryKey: ['customer_state', stateKey],
                queryFn: () => fetchGetStateByStateKey({ stateKey })
            });

            const newCustomerState = JSON.parse(JSON.stringify(customerState));

            newCustomerState.data.application.company.last_12_months_turnover =
                {
                    amount: company_turnover,
                    datetime: new Date().toISOString()
                };

            await saveCustomerStateMutation.mutateAsync(newCustomerState);

            await createApplicationMutation.mutateAsync(
                applicationWithoutTurnover
            );
        } catch (error) {
            if (error instanceof Error) {
                throw new Error("Couldn't create new application", error);
            }
            throw new Error(
                'Unknown error occurred while creating new application'
            );
        }
    };

    return { createNewApplication };
};
